import { borderRadius } from 'fitify-ui/src/theme/generated/theme-variables'
import { themeColors as figmaThemeColors } from 'fitify-ui-onboarding/src/theme/generated/theme-colors'
import {
  dynamicSizes,
  themeSizes,
} from 'fitify-ui-onboarding/src/theme/variables'
import {
  ColorTheme,
  FigmaColorTheme,
  type Theme,
} from 'fitify-ui-onboarding/src/types/theme'
import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { useTranslation } from 'next-i18next/pages'
import { useMemo } from 'react'

import {
  resolveF01ThemeVariant,
  resolveGymF01ThemeVariant,
  resolveM01ThemeVariant,
} from 'components/GenderFunnel/Layout/Layout.Utils'
import { useFunnel } from 'hooks/useFunnel'
import { getOnboardingFonts } from 'theme/fonts'
import { Funnel } from 'types/state'
import { useDataState } from 'utils/state'

export const useDefaultTheme = () => {
  const { i18n } = useTranslation()

  return useMemo(
    (): Theme => ({
      colors: figmaThemeColors[FigmaColorTheme.BlueLight],
      dynamicSizes,
      fonts: getOnboardingFonts(i18n.language),
      isDarkTheme: false,
      radius: borderRadius['Large'],
      sizes: themeSizes.genderSpecificFunnel,
      type: ColorTheme.BlueLight,
    }),
    [i18n.language]
  )
}

export const useLayoutTheme = () => {
  const growthBook = useGrowthBook()
  const funnel = useFunnel()
  const { data } = useDataState()
  const defaultTheme = useDefaultTheme()

  return useMemo((): Theme => {
    switch (funnel) {
      case Funnel.CHF01:
      case Funnel.CHM01:
        return {
          colors: figmaThemeColors[FigmaColorTheme.EmeraldGreenLight],
          dynamicSizes,
          fonts: defaultTheme.fonts,
          isDarkTheme: false,
          radius: borderRadius['Large'],
          sizes: themeSizes.genderSpecificFunnel,
          type: ColorTheme.EmeraldGreenLight,
        }
      case Funnel.M01: {
        return {
          ...resolveM01ThemeVariant(growthBook, data.common.onboardingVariant),
          dynamicSizes,
          fonts: defaultTheme.fonts,
          radius: borderRadius['Large'],
          sizes: themeSizes.genderSpecificFunnel,
        }
      }
      case Funnel.F01:
      case Funnel.HOPIF01:
      case Funnel.WALLPIF01: {
        return {
          ...resolveF01ThemeVariant(data.common.onboardingVariant),
          dynamicSizes,
          fonts: defaultTheme.fonts,
          radius: borderRadius['Large'],
          sizes: themeSizes.genderSpecificFunnel,
        }
      }
      case Funnel.GYMF01:
        return {
          ...resolveGymF01ThemeVariant(growthBook),
          dynamicSizes,
          fonts: defaultTheme.fonts,
          radius: borderRadius['Large'],
          sizes: themeSizes.genderSpecificFunnel,
        }
      case Funnel.GYMM01:
        return {
          colors: figmaThemeColors[FigmaColorTheme.BlueLight],
          dynamicSizes,
          fonts: defaultTheme.fonts,
          isDarkTheme: false,
          radius: borderRadius['Large'],
          sizes: themeSizes.genderSpecificFunnel,
          type: ColorTheme.BlueLight,
        }
      case Funnel.TCHF01:
      case Funnel.TCHM01:
        return {
          colors: figmaThemeColors[FigmaColorTheme.SoftPeriwinkleLight],
          dynamicSizes,
          fonts: defaultTheme.fonts,
          isDarkTheme: false,
          radius: borderRadius['Large'],
          sizes: themeSizes.genderSpecificFunnel,
          type: ColorTheme.SoftPeriwinkleLight,
        }
      case Funnel.MILICAF01:
      case Funnel.MILICAM01:
        return {
          colors: figmaThemeColors[FigmaColorTheme.SandMilitaryDark],
          dynamicSizes,
          fonts: defaultTheme.fonts,
          isDarkTheme: true,
          radius: borderRadius['Large'],
          sizes: themeSizes.genderSpecificFunnel,
          type: ColorTheme.SandMilitaryDark,
        }
      default:
        return defaultTheme
    }
  }, [data.common.onboardingVariant, defaultTheme, funnel, growthBook])
}
